import {
  Container,
  TextInputFormikField,
  Text,
  SectionMessage,
  ProgressLoader,
} from "@certn/cascadia-react";
import { BasicButton, Stack } from "@certn/cascadia";
import { showToast } from "@certn/cascadia";
import { Form, Formik } from "formik";
import { css } from "@emotion/css";
import { useContext } from "react";
import { Trans, useTranslation } from "react-i18next";

import { WorkableIntegrationContext } from "../../WorkableIntegrationContext";
import { WorkableIntegrationStepLayout } from "../WorkableIntegrationStepLayout";
import { WhatsThis } from "../../../components/WhatsThis";
import { useClientPortalSubmit } from "@certn/api";
import { useNavigate } from "react-router-dom";

export function WorkableApiKeyStep() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { atsIntegrationData } = useContext(WorkableIntegrationContext);

  const { submit: enableIntegration, isSubmitting } = useClientPortalSubmit(
    `/api/internal/client-portal/ats-integrations/${atsIntegrationData?.integration_id}/enable/`,
    {
      onSuccess: () => {
        showToast(
          t("Success, {{integration}} has been successfully enabled.", {
            integration: "Workable",
          }),
          {
            type: "success",
          }
        );
        navigate("/settings/ats-integrations");
      },
      showToastOnError: true,
    }
  );

  const handleSubmit = () => {
    enableIntegration();
  };

  return (
    <>
      <Formik initialValues={atsIntegrationData} onSubmit={handleSubmit}>
        {({ handleSubmit, isValid, values }) => {
          async function copyToClipBoard() {
            await navigator.clipboard.writeText(values?.api_key || "");
            showToast(t("API key copied to clipboard"), { type: "success" });
          }

          const formFields = (
            <>
              <Stack gap="space-6">
                <Stack gap="space-2">
                  <Text style="global/heading-medium">
                    {t("Details to add to your {{integration}} app", {
                      integration: "Workable",
                    })}
                  </Text>
                  <SectionMessage type="warning" icon="error">
                    <Trans>
                      You will not be able to view the API key again after you
                      complete the setup. You may want to store it somewhere
                      safe.
                    </Trans>
                  </SectionMessage>
                </Stack>
                <Stack orientation="horizontal" align="end" gap="space-2">
                  <TextInputFormikField
                    className={styles.textFieldSpacing}
                    name="api_key"
                    testID="workable-api-key"
                    label={t("API key")}
                    isDisabled
                    isRequired
                    info={
                      <WhatsThis>
                        <Trans>
                          Your API key enables Workable to create cases on your
                          behalf. Please enable Workable's Certn integration by
                          pasting this key. You can find the integration by
                          navigating to:
                          <br />
                          <br />
                          Settings → Integrations → Apps → Background checks →
                          Certn
                        </Trans>
                      </WhatsThis>
                    }
                  />
                  <BasicButton
                    onPress={copyToClipBoard}
                    type="button"
                    size="medium"
                    testID="copy-link-button"
                    leftIcon="content_copy"
                  >
                    {t("Copy")}
                  </BasicButton>
                </Stack>
              </Stack>
            </>
          );

          const formContent = (
            <Container
              layout="narrow"
              testID="workable-setup-page-outermost-container"
              className={styles.container}
            >
              <Form>
                <Stack gap="space-2">
                  <div>
                    <Text style="input/section-title">
                      {t("step {{stepNumber}} of {{stepCount}}", {
                        stepNumber: 2,
                        stepCount: 2,
                      })}
                    </Text>
                    <Text
                      as="h1"
                      className={styles.titleSpacing}
                      style="global/title-small"
                    >
                      {t("{{integration}} integration", {
                        integration: "Workable",
                      })}
                    </Text>
                  </div>
                  <ProgressLoader floating value={100} />
                </Stack>
                <div className={styles.mainContentContainer}>{formFields}</div>
              </Form>
            </Container>
          );

          return (
            <WorkableIntegrationStepLayout
              onComplete={handleSubmit}
              disableCompletion={!isValid}
              submitting={isSubmitting}
            >
              {formContent}
            </WorkableIntegrationStepLayout>
          );
        }}
      </Formik>
    </>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
    marginTop: "var(--space-4)",
  }),
  container: css({
    rowGap: "1rem !important",
  }),
  textFieldSpacing: css({ display: "flex", flex: "1" }),
};
